/**
 * Full text of a single notification.
 *
 * Stacked at z-[60] so it can open on top of the history modal (z-50),
 * matching the existing stacked-modal precedent in BatchList / WalletSettings.
 *
 * Opening this modal does NOT mark the notification read — the user has to
 * press the button. A mis-click should never silently clear a notice they
 * haven't actually read.
 */

import { X, ExternalLink } from 'lucide-react';
import type { ActiveNotification, HistoryNotification } from '../hooks/useNotifications';
import { severityStyle, formatNotificationDate } from './notificationStyles';

interface NotificationDetailModalProps {
  notification: (ActiveNotification | HistoryNotification) | null;
  isOpen: boolean;
  onClose: () => void;
  onMarkRead: (id: string) => void;
}

export default function NotificationDetailModal({
  notification,
  isOpen,
  onClose,
  onMarkRead,
}: NotificationDetailModalProps) {
  if (!isOpen || !notification) return null;

  const style = severityStyle(notification.severity);
  const Icon = style.Icon;

  // ActiveNotification has no is_read field — anything in the banner stack is
  // unread by definition.
  const isRead = 'is_read' in notification ? notification.is_read : false;

  const handleMarkRead = () => {
    onMarkRead(notification.id);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between gap-3 p-4 border-b border-gray-200">
          <div className="flex items-start gap-2 min-w-0">
            <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${style.icon}`} />
            <h2 className="text-lg font-semibold text-gray-900 break-words">
              {notification.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs text-gray-500 mb-3">
            {formatNotificationDate(notification.created_at)}
            {'is_expired' in notification && notification.is_expired && (
              <span className="ml-2 italic text-gray-400">· Expired</span>
            )}
          </p>

          {/* Plain text only — whitespace-pre-line preserves the admin's line
              breaks. Never dangerouslySetInnerHTML here: this string is
              rendered into every targeted customer's browser. */}
          <div className="text-sm text-gray-700 whitespace-pre-line break-words">
            {notification.body}
          </div>

          {notification.link_url && (
            <div className="mt-5 flex justify-center">
              <a
                href={notification.link_url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${style.button}`}
              >
                {notification.link_label || 'Learn more'}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end gap-3 p-4 border-t border-gray-200">
          {!isRead && (
            <button
              type="button"
              onClick={handleMarkRead}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${style.button}`}
            >
              Mark as read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
